import React from "react";
// Import logo resmi dari react-icons
import { FaGooglePlay, FaApple, FaGlobe } from "react-icons/fa6";

function ProjectCard({
  thumbnail,
  category,
  title,
  summary,
  businessImpact,
  techStack = [],
  caseStudyUrl = "#",
  siteUrl,
  playStoreUrl,
  appStoreUrl,
  isReversed = false,
  onDetailClick,
}) {
  return (
    // Penambahan 'group' pada pembungkus luar untuk mengontrol dinamika floating badge & gambar secara serentak
    <div className="border-2 border-slate-900 bg-white rounded-3xl p-6 lg:p-8 shadow-[8px_8px_0px_0px_#ccd5ae] hover:shadow-[12px_12px_0px_0px_#d4a373] transition-all duration-300 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center relative group">
      {/* ─── 1. KATEGORI FLOATING LEPAS (Keluar dari Frame Gambar & Memantul/Rotate saat Hover) ─── */}
      <span className="absolute -top-3 left-6 z-20 border-2 border-slate-900 bg-white px-3 py-1 text-[10px] font-black uppercase tracking-wider text-slate-900 rounded-md shadow-[2px_2px_0px_0px_#faedcd] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105 group-hover:shadow-[3px_3px_0px_0px_#d4a373]">
        {category}
      </span>

      {/* Kolom Visual Blueprint / Thumbnail */}
      <div
        className={`lg:col-span-5 w-full aspect-[16/10] bg-gsf-sage/10 border-2 border-slate-900 rounded-2xl overflow-hidden relative ${isReversed ? "lg:order-last" : ""}`}
      >
        {thumbnail ? (
          /* ─── 2. IMAGE PENUH (object-contain) & BERWARNA DEFAULT (Hanya transisi filter saat hover) ─── */
          <img
            src={thumbnail}
            alt={title}
            className="w-full h-full object-contain p-2 transition-all duration-500 group-hover:scale-[1.02] group-hover:contrast-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-grid-pattern opacity-60">
            <span className="font-mono text-xs text-slate-400">
              [ Visual Blueprint ]
            </span>
          </div>
        )}
      </div>

      {/* Kolom Konten */}
      <div className="lg:col-span-7 space-y-4 flex flex-col justify-between h-full">
        <div className="space-y-2">
          <h3 className="text-2xl lg:text-3xl font-black text-slate-900 tracking-tight leading-none group-hover:text-gsf-bronze transition-colors">
            {title}
          </h3>
          <p className="text-sm sm:text-base text-slate-600 font-medium leading-relaxed">
            {summary}
          </p>
        </div>

        {/* Business Impact Box */}
        <div className="bg-gsf-sand/30 border border-gsf-sand p-3.5 rounded-xl space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gsf-bronze block">
            🚀 Business Impact
          </span>
          <p className="text-xs sm:text-sm text-slate-700 font-semibold leading-relaxed">
            {businessImpact}
          </p>
        </div>

        {/* Technologies Badges */}
        <div className="space-y-1.5">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 block">
            Technologies Used
          </span>
          <div className="flex flex-wrap gap-1.5">
            {techStack.map((tech, index) => (
              <span
                key={index}
                className="border border-slate-200 bg-slate-50 text-slate-700 px-2 py-0.5 rounded text-[11px] font-medium font-mono"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* ─── GRUP TOMBOL AKSI DENGAN IKON RESMI SVG ─── */}
        <div className="pt-2 flex flex-wrap items-center gap-3">
          {/* 1. Tombol Detail Utama (Case Study) */}
          <button
            onClick={onDetailClick}
            type="button"
            className="inline-flex items-center border-2 border-slate-900 bg-slate-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-white rounded-xl shadow-[3px_3px_0px_0px_#faedcd] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#d4a373] active:translate-y-0"
          >
            Detail
          </button>

          {/* 2. Tombol Live Site */}
          {siteUrl && (
            <a
              href={siteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700 rounded-xl shadow-[3px_3px_0px_0px_#faedcd] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#ccd5ae] active:translate-y-0 hover:bg-gsf-cream"
            >
              <FaGlobe className="text-sm text-slate-600" />
              View Site
            </a>
          )}

          {/* 3. Tombol Google Play Store */}
          {playStoreUrl && (
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700 rounded-xl shadow-[3px_3px_0px_0px_#faedcd] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#ccd5ae] active:translate-y-0 hover:bg-gsf-cream"
            >
              <FaGooglePlay className="text-sm text-slate-900" />
              Play Store
            </a>
          )}

          {/* 4. Tombol Apple App Store */}
          {appStoreUrl && (
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-4 py-2.5 text-xs font-bold uppercase tracking-wider text-slate-700 rounded-xl shadow-[3px_3px_0px_0px_#faedcd] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[5px_5px_0px_0px_#ccd5ae] active:translate-y-0 hover:bg-gsf-cream"
            >
              <FaApple className="text-base text-slate-900" />
              App Store
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectCard;
